import React, { useState, useRef } from 'react';
import { Camera, X, Loader, Image as ImageIcon } from 'lucide-react';
import api from '../services/api';

/**
 * Composant d'upload de photo pour les clubs
 * Permet de prendre une photo ou de choisir depuis la pellicule
 */
function ClubPhotoUpload({ currentPhoto, onPhotoUpdate, clubId = null }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentPhoto || null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Gère la sélection de fichier
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError(null);
    setSuccess(false);

    // Validation de taille (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('La photo est trop grande (maximum 10MB)');
      return;
    }

    // Validation de type
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image valide');
      return;
    }

    // Prévisualisation locale immédiate
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload vers le serveur
    await uploadPhoto(file);
  };

  // Upload la photo vers le backend
  const uploadPhoto = async (file) => {
    setUploading(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('photo', file);

    try {
      const response = await api.post('/clubs/upload-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000
      });

      // Mise à jour avec l'URL Cloudinary
      const photoUrl = response.data.photoUrl;
      setPreview(photoUrl);
      onPhotoUpdate(photoUrl);
      setSuccess(true);
      
      // Cacher le message de succès après 3 secondes
      setTimeout(() => setSuccess(false), 3000);
      
      console.log('✅ Photo du club uploadée avec succès');
    } catch (error) {
      console.error('❌ Erreur upload:', error);
      
      if (error.response) {
        setError(error.response.data.message || 'Erreur lors de l\'upload');
      } else if (error.request) {
        setError('Impossible de contacter le serveur. Vérifie ta connexion.');
      } else {
        setError('Une erreur est survenue');
      }
      
      // Revenir à l'ancienne photo
      setPreview(currentPhoto);
    } finally {
      setUploading(false);
    }
  };

  // Supprimer la photo
  const handleDeletePhoto = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm('Voulez-vous vraiment supprimer cette photo ?')) {
      return;
    }

    setPreview(null);
    onPhotoUpdate(null);
    console.log('✅ Photo supprimée');
  };

  // Handler pour le bouton caméra
  const handleCameraClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      {/* Photo du club avec overlay */}
      <div className="relative group">
        {/* Container de la photo - Format rectangulaire pour les clubs */}
        <div className="w-full h-48 sm:h-64 rounded-2xl overflow-hidden bg-gray-200 border-4 border-white shadow-xl">
          {preview ? (
            <>
              <img
                src={preview}
                alt="Photo du club"
                className="w-full h-full object-cover"
              />
              {/* Overlay sombre au hover */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-2xl"></div>
            </>
          ) : (
            // Image par défaut si pas de photo
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100">
              <ImageIcon size={64} className="text-gray-400 mb-2" />
              <p className="text-gray-500 text-sm">Aucune photo</p>
            </div>
          )}
          
          {/* Loading overlay pendant l'upload */}
          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center rounded-2xl">
              <Loader className="animate-spin text-white mb-2" size={40} />
              <p className="text-white text-sm">Upload en cours...</p>
            </div>
          )}
        </div>

        {/* Bouton caméra en bas à droite */}
        <button
          type="button"
          onClick={handleCameraClick}
          disabled={uploading}
          className="absolute bottom-4 right-4 bg-indigo-600 text-white p-3 rounded-full shadow-lg hover:bg-indigo-700 transition-all transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          aria-label="Changer la photo du club"
        >
          {uploading ? (
            <Loader className="animate-spin" size={20} />
          ) : (
            <Camera size={20} />
          )}
        </button>

        {/* Bouton supprimer en haut à droite (si photo existe) */}
        {preview && !uploading && (
          <button
            type="button"
            onClick={handleDeletePhoto}
            className="absolute top-4 right-4 bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-all transform hover:scale-110"
            aria-label="Supprimer la photo"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Input file caché - Sans capture pour permettre pellicule + caméra */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Texte d'aide */}
      <div className="text-center space-y-2">
        <button
          type="button"
          onClick={handleCameraClick}
          disabled={uploading}
          className="text-indigo-600 hover:text-indigo-700 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {uploading 
            ? '⏳ Upload en cours...' 
            : preview 
              ? '📸 Changer la photo' 
              : '➕ Ajouter une photo'
          }
        </button>
        
        <p className="text-xs text-gray-500">
          JPG, PNG ou WebP • Max 10MB • Recommandé : 1200x800px
        </p>

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
            <p className="text-red-600 text-sm">⚠️ {error}</p>
          </div>
        )}

        {/* Message succès */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
            <p className="text-green-600 text-sm font-medium">✅ Photo mise à jour !</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClubPhotoUpload;